import { PartType } from '@models/editor/Part'
import InstrumentDescriptor, {
  InstrumentDescriptorType,
} from '@utils/nf-webaudio/instrument-descriptor'
import InstrumentManifest, { InstrumentManifestType } from '@utils/nf-webaudio/instrument-manifest'
import SampledInstrument, { SampledInstrumentType } from '@utils/nf-webaudio/sampled-instrument'
import SampledInstrumentZone from '@utils/nf-webaudio/sampled-instrument-zone'
import Sequencer, { SequencerOptions } from '@utils/nf-webaudio/sequencer'
import { Sequence } from '@utils/nf-webaudio/sequencer.types'
import SynthesisContext, {
  SynthContextOptions,
  SynthesisContextType,
} from '@utils/nf-webaudio/synthesis-context'
import utils, { UtilsType } from '@utils/nf-webaudio/utils'

interface globalSynthesisParametersType {
  reverbBuffer: object
  partsXmlSpecification: object
}

interface SynthesisContextSingletonType {
  getInstance: (options: SynthContextOptions) => Promise<SynthesisContextType>
  clear: () => void
}

interface nfsynType {
  DECIBELS_PER_DECADE: number
  MINIMUM_SIGNAL: number
  MINIMUM_CONTROL: number
  PLAYBACK_STOP_DECAY: number
  EXPORT_AUDIO_SAMPLE_RATE: number
  EXPORT_AUDIO_DECAY_EXTRA_TIME: number
  WARM_UP_DELAY: number
  globalSynthesisParameters: globalSynthesisParametersType
  SynthesisContextSingleton: SynthesisContextSingletonType
  SynthesisContext: (options: SynthContextOptions) => void
  InstrumentManifest: (synth: SynthesisContextType, options: SynthContextOptions) => void
  InstrumentDescriptor: (manifest: InstrumentManifestType, xml: Element) => void
  Sequencer: (
    audioContext: SynthesisContextType,
    manifest: InstrumentManifestType,
    result: Sequence,
    seqOpts: SequencerOptions
  ) => void
  SampledInstrument: (
    synth: SynthesisContextType,
    descriptor: InstrumentDescriptorType,
    part: PartType,
    previewFlag: boolean
  ) => void
  SampledInstrumentZone: (instrument: SampledInstrumentType) => void
  utils: UtilsType
  instrumentZoneCache: Record<string, any>
}

// Global parameters object
// It is created only once and it has the info that can be used for multiple Synthesis.
const globalSynthesisParameters = {
  // Object with all the reverb buffers that will be loaded.
  // We don't need to load the same twice if the user switches many times from one to another one, so just keep track of them.
  // The "key" is the reverbType (ex: "Large")
  reverbBuffer: {},

  // XML specification of the parts. It is used when we load the score for the first time and when we create an offlineAudioContext
  // The key is the descriptor.getUri() of the instrument
  partsXmlSpecification: {},
}

const SynthesisContextSingleton = (function () {
  let instance: SynthesisContextType | null

  return {
    getInstance: async function (options) {
      if (!instance) {
        const _instance: SynthesisContextType = new nfsyn.SynthesisContext(options)
        instance = await _instance.init()
        return instance
      }
      return instance
    },
    clear: function () {
      instance = null
    },
  }
})()

export const nfsyn: nfsynType = {
  DECIBELS_PER_DECADE: 20.0,
  MINIMUM_SIGNAL: 1.0 / 65536.0,
  MINIMUM_CONTROL: 1.0 / 128.0,
  PLAYBACK_STOP_DECAY: 0.1,
  EXPORT_AUDIO_SAMPLE_RATE: 44100,
  EXPORT_AUDIO_DECAY_EXTRA_TIME: 2, // Time in seconds that will be added at the end of the WAV file to let the lasts notes decay
  WARM_UP_DELAY: 0.3, // Hack for FF. The playback will be delayed this amount of time
  globalSynthesisParameters,
  SynthesisContextSingleton,
  SynthesisContext,
  InstrumentManifest,
  InstrumentDescriptor,
  Sequencer,
  SampledInstrument,
  SampledInstrumentZone,
  utils,
  instrumentZoneCache: {},
}

export default nfsyn
